//utils/apiFunction.ts
import axios from "axios";
import Cookies from "universal-cookie";
import { ICriteria } from "../types/Challenges.type";
const cookies = new Cookies();

interface ApiHeaders {
  apiKey?: string;
  userId?: string;
  token?: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
  userId: string;
  token: string;
}

interface UserAuth {
  userId?: string;
  token?: string;
}

const getAxiosHeaders = (headers: ApiHeaders) => ({
  "Content-Type": "application/json",
  ...headers,
});

export const getUserAuth = async ({
  BACKEND_URL,
  apiKey,
  entityId,
  uniqueUserId,
  uniqueEmailId,
  userId,
  token,
  GeneralFunctions,
  entityAuthenticationToken,
}: {
  BACKEND_URL: string;
  apiKey?: string;
  entityId: string;
  uniqueUserId?: string;
  uniqueEmailId?: string;
  userId?: string;
  token?: string;
  GeneralFunctions: any;
  entityAuthenticationToken?: string;
}): Promise<UserAuth | null> => {
  try {
    const externalUserId = cookies.get("externalUserId");
    const questUserId = cookies.get("questUserId");
    const questUserToken = cookies.get("questUserToken");

    if (
      externalUserId &&
      questUserId &&
      questUserToken &&
      (externalUserId === uniqueUserId || externalUserId === uniqueEmailId)
    ) {
      return {
        userId: questUserId,
        token: questUserToken,
      };
    }

    if (uniqueUserId) {
      const headers = getAxiosHeaders({
        apiKey,
        ...(token && { token }),
        ...(userId && { userId }),
        ...(!!entityAuthenticationToken && { "entity-authentication-token": entityAuthenticationToken }),
      });

      const body = {
        externalUserId: uniqueUserId,
        entityId,
        email: uniqueEmailId || null,
        ignoreMAU: true,
      };

      const response = await axios.post<ApiResponse<UserAuth>>(
        `${BACKEND_URL}api/users/external/login`,
        body,
        { headers }
      );

      if (!response.data?.success) {
        throw new Error("Invalid response from external login");
      }
      const { userId: newUserId, token: newToken } = response.data;
      const expires = new Date();
      expires.setHours(expires.getHours() + 12);

      cookies.set("externalUserId", uniqueUserId, { path: "/", expires });
      cookies.set("questUserId", newUserId, { path: "/", expires });
      cookies.set("questUserToken", newToken, { path: "/", expires });

      return {
        userId: newUserId,
        token: newToken,
      };
    }

    if (userId && token) {
      return { userId, token };
    }

    return null;
  } catch (error) {
    GeneralFunctions.captureSentryException(error);
    return null;
  }
};

export const fetchData = async ({
  BACKEND_URL,
  apiKey,
  apiSecret,
  entityId,
  questId,
  enableVariation,
  userId,
  token,
  uniqueUserId,
  uniqueEmailId,
  setCriterias,
  setQuestThemeData,
  setBrandTheme,
  GeneralFunctions,
  setCampaignVariationId,
  setIsVerify,
  setClaimStatus,
  setCampaignXp,
  setShowFooter,
}: {
  BACKEND_URL: string;
  apiKey?: string;
  apiSecret?: string;
  entityId: string;
  questId?: string;
  enableVariation?: boolean;
  userId?: string;
  token?: string;
  uniqueUserId?: string;
  uniqueEmailId?: string;
  setCriterias: Function;
  setQuestThemeData: Function;
  setBrandTheme: Function;
  GeneralFunctions: any;
  setCampaignVariationId: any;
  setIsVerify: any;
  setClaimStatus: any;
  setCampaignXp: any;
  setShowFooter: any;
}) => {
  try {
    const response = await axios.get(
      `${BACKEND_URL}api/v2/entities/${entityId}/campaigns/${questId}`,
      {
        headers: {
          apiKey,
          apisecret: apiSecret,
          userId,
          token,
        },
      }
    );

    if (!response.data?.success) {
      throw new Error("Failed to fetch campaign data");
    }
    if (response.status === 200) {
      const fetchedCriterias: ICriteria[] = [];
      setClaimStatus(response?.data?.data?.isClaimed)
      const xpReward = response?.data?.data?.rewards?.find(
        (reward: any) => reward.rewardType === "REWARD_XP" && reward.isEnabled
      );
      const campaignXp = xpReward ? xpReward.xp || 0 : 0;
      setCampaignXp(campaignXp);
      if (response.data?.data?.actions) {
        let isAnyCriteriaComplete = false;

        for (let action of response.data.data.actions) {
          fetchedCriterias.push({
            completed: action.isCompleted,
            actionId: action.actionId,
            title: action.title,
            metricCount: action.metadata?.threshold || 0,
            isLocked: !action.isEnabled,
            progressPercent: action.progressPercent,
            progressData: action.progressData,
            xp: action.xp || 0,
          });

          if (action.progressPercent == 100) {
            isAnyCriteriaComplete = true;
          }
        }

        if (isAnyCriteriaComplete) {
          setIsVerify(true);
        }
      }

      if (response.data.data.sdkConfig?.uiProps?.questThemeData) {
        setQuestThemeData(response.data.data.sdkConfig.uiProps.questThemeData);
      }

      if (response.data.data.sdkConfig?.uiProps?.BrandTheme?.properties) {
        setBrandTheme(response.data.data.sdkConfig.uiProps.BrandTheme?.properties);
      }

      if (response.data.data.sdkConfig?.uiProps?.showFooter !== undefined) {
        setShowFooter(response.data.data.sdkConfig.uiProps.showFooter);
      }

      setCriterias(fetchedCriterias);
      setCampaignVariationId?.(response?.data?.data?.campaignVariationId);
    } else {
      console.error("Unexpected status code:", response.status);
    }
  } catch (error) {
    console.error("Error fetching challenges data:", error);
    GeneralFunctions.captureSentryException(error);
  }
};

export const verifyActions = async ({
  BACKEND_URL,
  apiKey,
  apiSecret,
  entityId,
  questId,
  criterias,
  userId,
  token,
  campaignVariationId,
  GeneralFunctions,
}) => {
  try {
    const headers = {
      apiKey,
      apisecret: apiSecret,
      userId,
      token,
    };

    const response = await axios.post(
      `${BACKEND_URL}api/v2/entities/${entityId}/campaigns/${questId}/verify`,
      {
        actions: criterias,
        campaignVariationId,
      },
      { headers }
    );

    if (!response.data?.success) {
      throw new Error("Failed to verify actions");
    }
    return response.data;
  } catch (error) {
    return error?.response?.data;
    console.error("Error verifying actions:", error);
    GeneralFunctions.captureSentryException(error);
  }
}